import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Calculator, ArrowRight, ChevronDown, ChevronUp, Check, Timer,
  TrendingUp, Wallet, Clock, FileText, AlertTriangle, Share2
} from "lucide-react";

const COUNTRY_DATA = {
  DE: {
    label: "Deutschland", currency: "€", svRate: 20, taxBuffer: 28, holidays: 10,
    svLabel: "Kranken- & Rentenversicherung (ca. 20 %)",
    taxLabel: "Einkommensteuer-Puffer (ca. 28 %)",
    note: "In Deutschland zahlst du als Freelancer Krankenversicherung (GKV/PKV) komplett selbst und solltest freiwillig für die Rente vorsorgen. Dazu kommt die Einkommensteuer (Progression bis 45 %).",
  },
  AT: {
    label: "Österreich", currency: "€", svRate: 26.83, taxBuffer: 25, holidays: 13,
    svLabel: "SVS-Beiträge (26,83 %)",
    taxLabel: "Einkommensteuer-Puffer (ca. 25 %)",
    note: "In Österreich zahlst du als EPU 26,83 % SVS-Beiträge (Pension, Kranken- & Unfallversicherung) auf deinen Gewinn. Dazu kommt die progressive Einkommensteuer (0–55 %).",
  },
  CH: {
    label: "Schweiz", currency: "CHF", svRate: 10, taxBuffer: 18, holidays: 9,
    svLabel: "AHV/IV/EO (ca. 10 %)",
    taxLabel: "Steuer-Puffer (ca. 18 %, je nach Kanton)",
    note: "In der Schweiz zahlst du als Selbständiger ca. 10 % AHV/IV/EO-Beiträge. Die Steuerbelastung variiert stark je nach Kanton und Gemeinde – plane einen Puffer von 15–25 % ein.",
  },
};

function computeRate({ targetIncome, costs, vacationDays, sickDays, hoursPerDay, utilization, svRate, taxBuffer, holidays }) {
  const workDays = Math.max(0, 260 - vacationDays - sickDays - holidays);
  const billableHours = workDays * hoursPerDay * (utilization / 100);
  const deductionRate = (svRate + taxBuffer) / 100;
  const grossIncomeNeed = deductionRate < 1 ? targetIncome / (1 - deductionRate) : targetIncome;
  const revenueNeed = grossIncomeNeed + costs;
  const minRate = billableHours > 0 ? revenueNeed / billableHours : 0;
  const recommendedRate = minRate * 1.15;
  return { workDays, billableHours: Math.round(billableHours), revenueNeed, minRate, recommendedRate, dayRate: recommendedRate * hoursPerDay };
}

const EXAMPLE_PROFILES = [
  { label: "Entwickler/in, Deutschland", country: "DE", targetIncome: 60000, costs: 6000, utilization: 65 },
  { label: "Designer/in, Österreich", country: "AT", targetIncome: 45000, costs: 4000, utilization: 60 },
  { label: "Berater/in, Schweiz", country: "CH", targetIncome: 90000, costs: 8000, utilization: 70 },
];

const ASSUMPTION_SOURCES = {
  DE: "KV/PV: eigene Schätzung auf Basis GKV-Beitragssatz 14,6 % + Zusatzbeitrag + PV; ESt-Puffer nach §32a EStG.",
  AT: "SVS: 26,83 % (PV 18,5 % + KV 6,8 % + SV-Beitrag Selbständige 1,53 %), Quelle svs.at; ESt-Puffer Stufentarif §33 EStG.",
  CH: "AHV/IV/EO Selbständigerwerbende 10,0 % (ahv-iv.ch, Merkblatt 2.02); Steuer-Puffer je Kanton, Quelle ESTV-Steuerrechner.",
};

function fmtMoney(value, country) {
  const rounded = Math.round(value);
  if (country === "CH") {
    return `CHF ${rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")}`;
  }
  return `${rounded.toLocaleString("de-DE")} €`;
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        style={{ display: "flex" }} className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`faq-toggle-${question.slice(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {open ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />}
      </button>
      <p className={`text-gray-600 pb-4 text-sm leading-relaxed ${open ? "block" : "hidden"}`}>{answer}</p>
    </div>
  );
}

function NumberField({ label, value, onChange, suffix, testid, min = 0, max = 1000000, step = 1 }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1.5">{label}</label>
      <div className="flex items-stretch rounded-md border border-input bg-white focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <Input
          type="number" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="flex-1 min-w-0 border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" data-testid={testid}
        />
        {suffix && <span className="flex shrink-0 items-center border-l border-gray-200 px-3 text-sm text-gray-500 select-none">{suffix}</span>}
      </div>
    </div>
  );
}

export default function StundensatzRechner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const num = (key, fallback) => {
    const v = Number(searchParams.get(key));
    return Number.isFinite(v) && searchParams.has(key) ? v : fallback;
  };
  const [country, setCountry] = useState(COUNTRY_DATA[searchParams.get("land")] ? searchParams.get("land") : "DE");
  const [targetIncome, setTargetIncome] = useState(num("einkommen", 50000));
  const [costs, setCosts] = useState(num("kosten", 6000));
  const [vacationDays, setVacationDays] = useState(num("urlaub", 25));
  const [sickDays, setSickDays] = useState(num("krank", 8));
  const [hoursPerDay, setHoursPerDay] = useState(num("stunden", 8));
  const [utilization, setUtilization] = useState(num("auslastung", 65));
  const isSharedResult = searchParams.has("einkommen");
  const [svRate, setSvRate] = useState(num("sv", COUNTRY_DATA[country].svRate));
  const [taxBuffer, setTaxBuffer] = useState(num("steuer", COUNTRY_DATA[country].taxBuffer));
  const [embedCopied, setEmbedCopied] = useState(false);

  const data = COUNTRY_DATA[country];
  const changeCountry = (c) => { setCountry(c); setSvRate(COUNTRY_DATA[c].svRate); setTaxBuffer(COUNTRY_DATA[c].taxBuffer); };
  const embedSnippet = `<a href="https://ivoliq.io/stundensatz-rechner" title="Stundensatz-Rechner für Freelancer (DE/AT/CH)">Stundensatz-Rechner für Freelancer – kostenlos bei ivoliq</a>`;
  const copyEmbed = async () => {
    try { await navigator.clipboard.writeText(embedSnippet); setEmbedCopied(true); toast.success("Einbettungs-Code kopiert"); setTimeout(() => setEmbedCopied(false), 2500); }
    catch { window.prompt("Code kopieren:", embedSnippet); }
  };

  useSEO({
    title: "Stundensatz-Rechner für Freelancer – DE, AT & CH | ivoliq",
    description: "Berechne deinen Mindest-Stundensatz als Freelancer: Wunschgehalt, Sozialversicherung, Steuern & Auslastung. Annahmen anpassbar, Ergebnis teilbar.",
    canonical: "https://ivoliq.io/stundensatz-rechner",
  });

  const result = useMemo(() => computeRate({ targetIncome, costs, vacationDays, sickDays, hoursPerDay, utilization, svRate, taxBuffer, holidays: data.holidays }),
    [targetIncome, costs, vacationDays, sickDays, hoursPerDay, utilization, svRate, taxBuffer, data]);

  const shareResult = async () => {
    const params = new URLSearchParams({
      land: country,
      einkommen: String(targetIncome),
      kosten: String(costs),
      urlaub: String(vacationDays),
      krank: String(sickDays),
      stunden: String(hoursPerDay),
      auslastung: String(utilization),
      sv: String(svRate),
      steuer: String(taxBuffer),
    });
    const url = `${window.location.origin}/stundensatz-rechner?${params.toString()}`;
    const text = `Mein empfohlener Stundensatz als Freelancer: ${fmtMoney(result.recommendedRate, country)} – berechne deinen:`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Stundensatz-Rechner | ivoliq", text, url });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopiert! Teile dein Ergebnis mit anderen Freelancern.");
    } catch {
      window.prompt("Link zum Teilen kopieren:", url);
    }
  };


  const faqItems = [
    {
      q: "Wie berechne ich meinen Stundensatz als Freelancer?",
      a: "Die Formel: (Wunsch-Nettoeinkommen geteilt durch (1 minus Abgabenquote) plus Betriebskosten) geteilt durch fakturierbare Stunden pro Jahr. Fakturierbare Stunden = Arbeitstage minus Urlaub, Krankheit und Feiertage, multipliziert mit Stunden pro Tag und deiner Auslastung. Unser Rechner macht das automatisch für DE, AT und CH.",
    },
    {
      q: "Warum ist mein Stundensatz so viel höher als ein Angestellten-Gehalt?",
      a: "Als Freelancer trägst du Kosten, die sonst der Arbeitgeber übernimmt: komplette Sozialversicherung, unbezahlten Urlaub, Krankheitstage, Akquise-Zeit, Weiterbildung, Ausstattung und das unternehmerische Risiko. Faustregel: Dein Stundensatz sollte mindestens das 1,5- bis 2-fache eines vergleichbaren Angestellten-Stundenlohns betragen.",
    },
    {
      q: "Was bedeutet Auslastung / fakturierbare Stunden?",
      a: "Nicht jede Arbeitsstunde ist abrechenbar. Akquise, Buchhaltung, E-Mails, Weiterbildung und Leerlauf sind unbezahlt. Realistisch sind 60–75 % Auslastung – d. h. von 8 Arbeitsstunden kannst du im Schnitt nur 5–6 einem Kunden in Rechnung stellen.",
    },
    {
      q: "Welcher Stundensatz ist in Österreich üblich?",
      a: "Österreichische EPU verrechnen je nach Branche 60–160 € pro Stunde. Wichtig: Von deinem Honorar gehen 26,83 % SVS-Beiträge plus Einkommensteuer ab. Ein Stundensatz unter 60 € ist für Vollzeit-Selbständige selten nachhaltig.",
    },
    {
      q: "Welcher Stundensatz ist in der Schweiz üblich?",
      a: "Schweizer Freelancer verrechnen je nach Branche CHF 90–250 pro Stunde. Die Lebenshaltungskosten und das Lohnniveau sind höher als in DE/AT – kalkuliere entsprechend. AHV/IV/EO-Beiträge von ca. 10 % und kantonale Steuern musst du selbst abführen.",
    },
    {
      q: "Sollte ich Tagessätze oder Stundensätze anbieten?",
      a: "Tagessätze (üblich: 7–8 × Stundensatz) eignen sich für längere Projekte und reduzieren Kleinklein-Diskussionen. Stundensätze passen für Support, Wartung und kleinere Aufgaben. Viele Freelancer kombinieren beides – wichtig ist eine saubere Zeiterfassung als Nachweis.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const toolSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "ivoliq Stundensatz-Rechner",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://ivoliq.io/stundensatz-rechner",
    description: "Kostenloser Stundensatz-Rechner für Freelancer in Deutschland, Österreich und der Schweiz.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-gray-900" data-testid="header-logo">ivoliq</Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">Anmelden</Link>
            <Button size="sm" onClick={() => navigate("/signup")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="header-cta">
              Kostenlos starten
            </Button>
          </div>
        </div>
      </header>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(toolSchema) }} />

      {/* HERO */}
      <section className="pt-16 pb-10 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <Calculator className="h-4 w-4" /> Kostenloser Rechner für DE / AT / CH
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            Stundensatz-Rechner<br />
            <span className="text-emerald-600">für Freelancer</span>
          </h1>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            Berechne in 60 Sekunden, was deine Stunde wirklich kosten muss –
            inklusive Sozialversicherung, Steuern, Urlaub und realistischer Auslastung.
          </p>
        </div>
      </section>

      {/* ==================== RECHNER ==================== */}
      <section className="pb-16" data-testid="rate-calculator-section">
        <div className="max-w-5xl mx-auto px-4">
          <Card className="border shadow-lg">
            <CardContent className="p-6 sm:p-8">
              {isSharedResult && (
                <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="shared-result-banner">
                  <Share2 className="h-4 w-4 flex-shrink-0" />
                  <span>Jemand hat diese Berechnung mit dir geteilt – passe die Werte einfach an deine Situation an.</span>
                </div>
              )}
              {/* Länder-Umschalter */}
              <div className="flex justify-center gap-2 mb-8" data-testid="country-switcher">
                {Object.entries(COUNTRY_DATA).map(([code, d]) => (
                  <button
                    key={code}
                    onClick={() => changeCountry(code)}
                    data-testid={`country-tab-${code}`}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                      country === code
                        ? "bg-emerald-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Eingaben */}
                <div className="space-y-5">
                  <NumberField
                    label={`Wunsch-Nettoeinkommen pro Jahr`}
                    value={targetIncome} onChange={setTargetIncome}
                    suffix={data.currency} step={1000} testid="input-target-income"
                  />
                  <NumberField
                    label="Betriebskosten pro Jahr (Software, Büro, Versicherung …)"
                    value={costs} onChange={setCosts}
                    suffix={data.currency} step={500} testid="input-costs"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <NumberField label="Urlaubstage" value={vacationDays} onChange={setVacationDays} max={60} testid="input-vacation" />
                    <NumberField label="Krankheitstage" value={sickDays} onChange={setSickDays} max={60} testid="input-sick" />
                  </div>
                  <NumberField label="Arbeitsstunden pro Tag" value={hoursPerDay} onChange={setHoursPerDay} min={1} max={14} testid="input-hours-per-day" />
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5">
                      Fakturierbare Auslastung: <span className="text-emerald-600 font-bold">{utilization} %</span>
                    </label>
                    <Slider
                      value={[utilization]} min={30} max={100} step={5}
                      onValueChange={(v) => setUtilization(v[0])}
                      data-testid="input-utilization"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Realistisch: 60–75 % (Akquise, Admin & Leerlauf sind unbezahlt)
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid="assumptions-box">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Abgaben-Annahmen für {data.label} (anpassbar)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <NumberField label="Sozialversicherung" value={svRate} onChange={setSvRate} suffix="%" testid="input-sv-rate" max={60} step={0.1} />
                      <NumberField label="Steuer-Puffer" value={taxBuffer} onChange={setTaxBuffer} suffix="%" testid="input-tax-buffer" max={60} step={0.5} />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">{ASSUMPTION_SOURCES[country]}</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{data.note}</span>
                  </div>
                </div>

                {/* Ergebnis */}
                <div className="bg-gray-900 rounded-xl p-6 text-white flex flex-col justify-between" data-testid="rate-result">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Dein empfohlener Stundensatz</p>
                    <p className="text-5xl font-bold text-emerald-400" data-testid="result-recommended-rate">
                      {fmtMoney(result.recommendedRate, country)}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">inkl. 15 % Risiko-Puffer</p>

                    <div className="mt-6 space-y-3 text-sm">
                      <div className="flex justify-between border-b border-gray-700 pb-2">
                        <span className="text-gray-400">Mindest-Stundensatz</span>
                        <span className="font-semibold" data-testid="result-min-rate">{fmtMoney(result.minRate, country)}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-700 pb-2">
                        <span className="text-gray-400">Empfohlener Tagessatz ({hoursPerDay} h)</span>
                        <span className="font-semibold" data-testid="result-day-rate">{fmtMoney(result.dayRate, country)}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-700 pb-2">
                        <span className="text-gray-400">Benötigter Jahresumsatz</span>
                        <span className="font-semibold" data-testid="result-revenue-need">{fmtMoney(result.revenueNeed, country)}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-700 pb-2">
                        <span className="text-gray-400">Fakturierbare Stunden / Jahr</span>
                        <span className="font-semibold" data-testid="result-billable-hours">{result.billableHours} h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Berücksichtigte Abgaben</span>
                        <span className="font-semibold">{(data.svRate + data.taxBuffer).toFixed(1).replace(".0", "")} %</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-4">
                      Sozialversicherung {svRate} % + Steuer-Puffer {taxBuffer} %. Richtwerte, keine Steuerberatung.
                    </p>
                  </div>
                  <div className="mt-6 space-y-2">
                    <Button
                      onClick={() => navigate("/signup")}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                      data-testid="calculator-cta"
                    >
                      Stunden tracken & abrechnen mit ivoliq <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <Button
                      onClick={shareResult}
                      variant="outline"
                      className="w-full bg-transparent border-gray-600 text-gray-200 hover:bg-gray-800 hover:text-white"
                      data-testid="share-result-button"
                    >
                      <Share2 className="h-4 w-4 mr-2" /> Ergebnis als Link teilen
                    </Button>
                    <details className="text-xs text-gray-400" data-testid="embed-details">
                      <summary className="cursor-pointer select-none py-1 hover:text-gray-200">Rechner in deinem Blog verlinken / einbetten</summary>
                      <textarea readOnly value={embedSnippet} rows={3} className="mt-2 w-full rounded-md bg-gray-800 p-2 font-mono text-[11px] text-gray-200" data-testid="embed-snippet" />
                      <button type="button" onClick={copyEmbed} className="mt-1 text-emerald-400 hover:text-emerald-300" data-testid="embed-copy-button">{embedCopied ? "Kopiert ✓" : "Code kopieren"}</button>
                    </details>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ==================== SO FUNKTIONIERT DIE FORMEL ==================== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            So funktioniert die Stundensatz-Formel
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Wallet, title: "1. Jahresbedarf", desc: "Dein Wunsch-Nettoeinkommen wird um Sozialversicherung und Steuer-Puffer hochgerechnet. Dazu kommen deine Betriebskosten. Ergebnis: der Umsatz, den du pro Jahr erwirtschaften musst." },
              { icon: Clock, title: "2. Fakturierbare Stunden", desc: "Von 260 möglichen Arbeitstagen ziehen wir Urlaub, Krankheit und Feiertage ab. Die verbleibenden Stunden werden mit deiner Auslastung multipliziert – denn Akquise & Admin bezahlt dir niemand." },
              { icon: TrendingUp, title: "3. Dein Stundensatz", desc: "Jahresbedarf geteilt durch fakturierbare Stunden ergibt deinen Mindest-Stundensatz. Wir empfehlen +15 % Puffer für Preisverhandlungen, Zahlungsausfälle und Investitionen." },
            ].map((s) => (
              <div key={s.title} className="bg-white rounded-xl p-6 border shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                  <s.icon className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-8 leading-relaxed max-w-3xl mx-auto text-center">
            Der häufigste Fehler von Freelancern: Sie rechnen ihr altes Angestellten-Gehalt einfach in einen Stundensatz um.
            Dabei vergessen sie, dass der Arbeitgeber bisher rund die Hälfte der Sozialabgaben getragen hat – und dass Urlaub,
            Krankheit, Akquise und Buchhaltung als Selbständiger unbezahlte Zeit sind. Das Ergebnis: chronische Unterbezahlung.
            Unser Rechner deckt genau diese versteckten Kosten auf.
          </p>
        </div>
      </section>

      {/* ==================== BENCHMARKS ==================== */}
      <section className="py-16 bg-white" data-testid="examples-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">
            Drei Beispiel-Profile – so rechnet der Rechner
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-xl mx-auto text-sm">
            Keine Marktdaten, sondern nachvollziehbare Rechnung mit den Standard-Annahmen (25 Tage Urlaub, 8 Krankheitstage, 8 h/Tag). Klick auf ein Profil übernimmt die Werte in den Rechner.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {EXAMPLE_PROFILES.map((p) => {
              const d = COUNTRY_DATA[p.country];
              const r = computeRate({ targetIncome: p.targetIncome, costs: p.costs, vacationDays: 25, sickDays: 8, hoursPerDay: 8, utilization: p.utilization, svRate: d.svRate, taxBuffer: d.taxBuffer, holidays: d.holidays });
              return (
                <button type="button" key={p.label} data-testid={`example-profile-${p.country}`}
                  onClick={() => { changeCountry(p.country); setTargetIncome(p.targetIncome); setCosts(p.costs); setVacationDays(25); setSickDays(8); setHoursPerDay(8); setUtilization(p.utilization); document.querySelector('[data-testid="rate-calculator-section"]')?.scrollIntoView({ behavior: "smooth" }); }}
                  className="text-left rounded-xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-lg hover:border-emerald-300">
                  <p className="text-sm font-semibold text-gray-900">{p.label}</p>
                  <p className="text-xs text-gray-500 mt-1">Zielnetto {fmtMoney(p.targetIncome, p.country)} · Kosten {fmtMoney(p.costs, p.country)} · {p.utilization} % Auslastung</p>
                  <p className="mt-3 text-2xl font-bold text-emerald-700">{fmtMoney(r.recommendedRate, p.country)}<span className="text-sm font-normal text-gray-500"> / h</span></p>
                  <p className="text-xs text-gray-500">{r.billableHours} fakturierbare Stunden, Abgaben {d.svRate + d.taxBuffer} %</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== BRIDGE ZU ZEITERFASSUNG ==================== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <Timer className="h-4 w-4" /> Der nächste Schritt
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Stundensatz berechnet – und jetzt jede Stunde erfassen
          </h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Der beste Stundensatz nützt nichts, wenn du Stunden vergisst. Mit der{" "}
            <Link to="/zeiterfassung-freelancer" className="text-emerald-600 underline font-medium">Zeiterfassung von ivoliq</Link>{" "}
            trackst du jede Minute und machst daraus{" "}
            <Link to="/zeiterfassung-freelancer#rechnung" className="text-emerald-600 underline font-medium">mit einem Klick eine Rechnung</Link> – dein Stundensatz wird automatisch übernommen.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-left mb-8">
            {[
              { icon: Timer, text: "One-Click Timer mit Projektzuordnung" },
              { icon: FileText, text: "Rechnung aus Zeiteinträgen mit einem Klick" },
              { icon: Check, text: "Kostenlos für DE, AT & CH" },
            ].map((f, i) => (
              <div key={i} className="bg-white rounded-lg p-4 border flex items-start gap-3">
                <f.icon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{f.text}</span>
              </div>
            ))}
          </div>
          <Button size="lg" onClick={() => navigate("/signup")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="bridge-cta">
            Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-16 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Häufige Fragen zum Stundensatz
          </h2>
          <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="faq-list">
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== INTERNAL LINKS ==================== */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-600 mb-4">Auch interessant für dich:</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/zeiterfassung-freelancer" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Zeiterfassung für Freelancer</Link>
            <Link to="/zeiterfassung-freelancer#rechnung" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Von der Zeiterfassung zur Rechnung</Link>
            <Link to="/zeiterfassung-freelancer-oesterreich" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Zeiterfassung in Österreich</Link>
            <Link to="/steuerruecklagen-freelancer" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Steuerrücklagen-Rechner</Link>
            <Link to="/rechnungsvorlage-freelancer" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Rechnungsvorlagen</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
